import { useContext } from 'react';

import {
  Avatar,
  Box,
  Flex,
  Stack,
  Text,
  useColorModeValue,
  useColorMode,
  Image,
} from '@chakra-ui/react';

import Links from 'layouts/admin/sidebar/components/Links';
import { HSeparator } from 'components/separator/Separator';

import { AuthContext } from 'contexts/AuthContext';

import BigLogo from 'assets/img/others/big_precision_x.png';
import MyChoiceLogo from 'assets/img/others/my_choice.webp';
import PolicyPilotLogo from 'assets/img/others/policy_pilot.png';
import MyChoiceDarkLogo from 'assets/img/others/mychoice_dark_mode.webp';

import { ROLES_OPTIONS } from 'variables/options';

import {
  ORGANISATION_THE_BIG_DEV,
  ORGANISATION_THE_BIG_PR,
  ORGANISATION_THE_BIG,
  ORGANISATION_DEVELOPMENT,
} from 'config';

const ENVIRONMENT_BANNERS: Record<string, { label: string; bg: string }> = {
  [ORGANISATION_THE_BIG_DEV]: { label: 'Dev environment', bg: 'blue.500' },
  [ORGANISATION_THE_BIG_PR]: { label: 'PR environment', bg: 'purple.500' },
};

const SidebarContent = ({ routes }: { routes: RoutesType[] }) => {
  const { user, environment } = useContext(AuthContext);
  const { colorMode } = useColorMode();
  const textColor = useColorModeValue('navy.700', 'white');
  const avatarBg = useColorModeValue('gray.700', 'gray.600');
  const environmentBanner = ENVIRONMENT_BANNERS[environment];

  const getLogo = () => {
    if (
      [
        ORGANISATION_THE_BIG_DEV,
        ORGANISATION_THE_BIG_PR,
        ORGANISATION_THE_BIG,
      ].includes(environment)
    ) {
      // Use dark mode logo if available, otherwise use default
      return colorMode === 'dark' ? MyChoiceDarkLogo : BigLogo;
    }

    if (environment === ORGANISATION_DEVELOPMENT) {
      return colorMode === 'dark' ? MyChoiceDarkLogo : MyChoiceLogo;
    }

    // Use dark mode logo if available, otherwise use default
    return colorMode === 'dark' ? MyChoiceDarkLogo : PolicyPilotLogo;
  };

  return (
    <Flex
      direction="column"
      alignItems="center"
      height="100%"
      borderRadius="30px"
    >
      {environmentBanner && (
        <Box
          w="100%"
          bg={environmentBanner.bg}
          color="white"
          p={3}
          textAlign="center"
          fontWeight="bold"
        >
          {environmentBanner.label}
        </Box>
      )}
      {user?.is_impersonated && (
        <Box
          w="100%"
          bg="orange.400"
          color="white"
          p={3}
          textAlign="center"
          fontWeight="bold"
        >
          Impersonating another user
        </Box>
      )}
      <Flex alignItems="center" flexDirection="column" pt="25px">
        <Image
          src={getLogo()}
          alt="policypilot logo"
          padding="20px 20px 0px 20px"
        />
        <HSeparator my="25px" w="65%" />
      </Flex>
      <Stack
        direction="column"
        mb="auto"
        overflowY="auto"
        sx={{
          '&::-webkit-scrollbar': {
            display: 'none',
          },
          scrollbarWidth: 'none',
        }}
      >
        <Box ms="unset" w="259px">
          <Links routes={routes} />
        </Box>
      </Stack>
      <Flex mt="30px" mb="55px" justifyContent="center" alignItems="center">
        <Avatar
          h="48px"
          w="48px"
          src={user.organization.logo}
          me={'20px'}
          bg={avatarBg}
        />
        <Box display="block">
          <Text color={textColor} fontSize="md" fontWeight="700">
            {user.organization.name}
          </Text>
          <Text color="secondaryGray.600" fontSize="sm" fontWeight="400">
            {`${user.first_name} ${user.last_name}`}
            <br />
            {ROLES_OPTIONS.find(
              (option) => option.value === user.role.toString(),
            )?.label || 'Unknown role'}
          </Text>
        </Box>
      </Flex>
    </Flex>
  );
};

export default SidebarContent;
